import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Activity, CheckCircle2, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';

const API = '/api';

/**
 * Blok 90 — Stripe live↔Mongo reconciliation card.
 * Reads the latest sanitized report stored by the Route B startup hook.
 * Admin-session gated (uses existing /admin auth cookie). No PII, no
 * secrets, no Bearer key needed.
 */
export default function Blok90ReconciliationCard({ token }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/commercial/reconciliation-latest`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setReport(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'fetch_failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [token]);

  const runNow = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/commercial/run-reconciliation-now`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res?.data?.ok === false && res?.data?.reason === 'rate_limited') {
        setError(`Wacht even, max 1× per ${res.data.retry_after_seconds}s`);
      } else if (res?.data?.latest_report) {
        setReport({ ok: true, latest_report: res.data.latest_report });
      } else {
        await load();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'run_failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }, [token, load]);

  useEffect(() => { load(); }, [load]);

  // Self-heal happens server-side now (Fase 2.6). Client still falls back
  // to runNow if the backend explicitly says no report exists.
  useEffect(() => {
    if (report && report.ok === false && report.reason === 'no_reconciliation_report_yet' && !loading) {
      runNow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const r = report?.latest_report;
  const isLive = r?.mode_detected === 'live';
  const sessions = r?.stripe?.checkout_sessions_complete_count ?? 0;
  const piSucceeded = r?.stripe?.payment_intents_succeeded_count ?? 0;
  const activeSubs = r?.stripe?.subscriptions_active_count ?? 0;
  const msPaid = r?.mongo?.monitoring_subscriptions_paid ?? 0;
  const msCsLive = r?.mongo?.monitoring_subscriptions_cs_live_count ?? 0;
  const msCsTest = r?.mongo?.monitoring_subscriptions_cs_test_count ?? 0;
  const whLive = r?.mongo?.stripe_webhook_log_livemode_true ?? 0;
  const whTotal = r?.mongo?.stripe_webhook_log_total ?? 0;
  const feCompleted = r?.mongo?.funnel_events_payment_completed_window ?? 0;
  const raSnapshots = r?.mongo?.revenue_attribution_snapshots_window ?? 0;
  const reconLive = r?.reconciliation?.live_sessions_total ?? 0;
  const reconMs = r?.reconciliation?.found_in_monitoring_subscriptions ?? 0;
  const reconFe = r?.reconciliation?.found_in_funnel_events ?? 0;
  const reconRa = r?.reconciliation?.found_in_revenue_attribution ?? 0;
  const hint = r?.root_cause_hint;
  const advice = r?.owner_p0_advice;
  const ranAt = r?.ran_at;
  const autoHealed = report?.auto_self_healed;
  const wasStale = report?.was_stale;

  // Verdict-based color theme
  const verdict = advice?.verdict || '';
  const verdictColor = (() => {
    if (verdict === 'healthy') return { bg: 'bg-emerald-500/15', border: 'border-emerald-400/40', text: 'text-emerald-200' };
    if (verdict === 'test_mode_environment' || verdict === 'live_but_empty') return { bg: 'bg-amber-500/15', border: 'border-amber-400/40', text: 'text-amber-200' };
    if (verdict === 'webhook_not_landing' || verdict === 'webhook_lands_mongo_update_fails') return { bg: 'bg-rose-500/15', border: 'border-rose-400/40', text: 'text-rose-200' };
    if (verdict === 'partial_attribution_gap') return { bg: 'bg-orange-500/15', border: 'border-orange-400/40', text: 'text-orange-200' };
    return { bg: 'bg-white/5', border: 'border-white/15', text: 'text-white/80' };
  })();

  return (
    <div
      className="rounded-2xl border border-white/10 p-5 shadow-2xl text-white"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="blok90-reconciliation-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-300" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-white/85">
            Blok 90 — Stripe ↔ Mongo Reconciliation
          </h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs px-2.5 py-1 bg-white/5 hover:bg-white/10 border border-white/15 rounded-md text-white/75 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="blok90-refresh-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Ververs
        </button>
        <button
          onClick={runNow}
          disabled={loading}
          className="text-xs px-2.5 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/40 rounded-md text-emerald-200 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="blok90-run-now-btn"
          title="Voer reconciliation nu uit (admin-session, rate-limited, read-only)"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
          Run nu
        </button>
      </div>

      {error && (
        <div className="text-xs text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded-md p-2 mb-3 inline-flex items-start gap-1.5" data-testid="blok90-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>Fout bij laden: {error}</span>
        </div>
      )}

      {!error && !report && !loading && (
        <div className="text-xs text-white/55 py-2">Geen rapport beschikbaar.</div>
      )}

      {report && !report.ok && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-md p-2 mb-2" data-testid="blok90-no-report">
          {report.reason || 'Nog geen reconciliation report aanwezig.'}{' '}
          {report.owner_action && <span className="text-white/70">— {report.owner_action}</span>}
        </div>
      )}

      {r && (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-3 text-[11px]">
            <span className={`px-2 py-0.5 rounded-md border ${isLive ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200' : 'bg-amber-500/15 border-amber-400/40 text-amber-200'}`} data-testid="blok90-mode">
              mode: <span className="font-semibold">{r.mode_detected || 'unknown'}</span>
            </span>
            <span className="px-2 py-0.5 rounded-md border bg-white/5 border-white/15 text-white/70">
              window: {r.window_days}d
            </span>
            <span className="px-2 py-0.5 rounded-md border bg-white/5 border-white/15 text-white/70">
              db: {r.mongo_db_name}
            </span>
            <span className="px-2 py-0.5 rounded-md border bg-white/5 border-white/15 text-white/55">
              ran_at: {ranAt ? new Date(ranAt).toLocaleString('nl-NL') : '—'}
            </span>
            {autoHealed && (
              <span className="px-2 py-0.5 rounded-md border bg-cyan-500/15 border-cyan-400/40 text-cyan-200" data-testid="blok90-auto-healed">
                auto-ververst
              </span>
            )}
            {wasStale && !autoHealed && (
              <span className="px-2 py-0.5 rounded-md border bg-amber-500/15 border-amber-400/40 text-amber-200" data-testid="blok90-stale">
                stale &gt;15min
              </span>
            )}
          </div>

          {advice && (
            <div
              className={`rounded-xl border ${verdictColor.bg} ${verdictColor.border} p-3 mb-3`}
              data-testid="blok90-owner-advice"
            >
              <div className={`text-[10px] uppercase tracking-wider ${verdictColor.text} mb-1`}>
                P0 oordeel — {advice.verdict.replace(/_/g, ' ')}
              </div>
              <div className={`text-sm font-semibold ${verdictColor.text} mb-2`} data-testid="blok90-advice-headline">
                {advice.headline}
              </div>
              <div className="text-[12px] text-white/80 leading-relaxed mb-2" data-testid="blok90-advice-explanation">
                {advice.explanation}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <div className="bg-black/25 rounded-md p-2 border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-white/55 mb-0.5">P0 fix (in begrijpelijke taal)</div>
                  <div className="text-[11px] text-white/85" data-testid="blok90-advice-fix">{advice.p0_fix_plain_nl}</div>
                </div>
                <div className="bg-black/25 rounded-md p-2 border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-white/55 mb-0.5">Veilige auto-actie</div>
                  <div className="text-[11px] text-white/85" data-testid="blok90-advice-auto">{advice.safe_auto_action}</div>
                </div>
                <div className="bg-black/25 rounded-md p-2 border border-white/5">
                  <div className="text-[10px] uppercase tracking-wider text-white/55 mb-0.5">Owner-beslissing nodig</div>
                  <div className="text-[11px] text-white/85" data-testid="blok90-advice-decision">{advice.owner_decision_needed}</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <Tile label="Stripe sessions (complete)" value={sessions} testid="blok90-stripe-sessions" />
            <Tile label="Stripe PI succeeded" value={piSucceeded} testid="blok90-pi-succeeded" />
            <Tile label="Stripe active subs" value={activeSubs} testid="blok90-active-subs" />
            <Tile label="Mongo MS paid" value={msPaid} testid="blok90-ms-paid" highlight={msPaid > 0} />
            <Tile label="Mongo MS cs_live" value={msCsLive} testid="blok90-cs-live" highlight={msCsLive > 0} />
            <Tile label="Mongo MS cs_test" value={msCsTest} testid="blok90-cs-test" />
            <Tile label="Webhook live events" value={whLive} testid="blok90-webhook-live" highlight={whLive > 0} />
            <Tile label="Webhook total" value={whTotal} testid="blok90-webhook-total" />
            <Tile label="funnel payment_completed" value={feCompleted} testid="blok90-fe-completed" />
            <Tile label="revenue snapshots" value={raSnapshots} testid="blok90-ra-snapshots" />
            <Tile label="recon live sessions" value={reconLive} testid="blok90-recon-live" />
            <Tile label="recon in MS / FE / RA" value={`${reconMs} / ${reconFe} / ${reconRa}`} testid="blok90-recon-coverage" />
          </div>

          {hint && (
            <div className="text-[11px] text-white/75 bg-white/5 border border-white/10 rounded-md p-2.5" data-testid="blok90-hint">
              <div className="flex items-center gap-1.5 text-white/55 mb-1 uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" /> Root-cause hint
              </div>
              <div>{hint}</div>
            </div>
          )}

          <details className="mt-2 text-[11px] text-white/55">
            <summary className="cursor-pointer hover:text-white/85">Raw sanitized report (klik om uit te klappen)</summary>
            <pre className="mt-2 p-2 bg-black/30 rounded-md overflow-auto text-[10px] text-white/65 max-h-60" data-testid="blok90-raw-json">
              {JSON.stringify(r, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, testid, highlight }) {
  return (
    <div
      className={`rounded-xl border p-2.5 ${highlight ? 'bg-emerald-500/15 border-emerald-400/40' : 'bg-white/5 border-white/10'}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/55 truncate">{label}</div>
      <div className={`mt-0.5 text-base font-semibold ${highlight ? 'text-emerald-200' : 'text-white'}`}>{value}</div>
    </div>
  );
}
